
import { defineComponent, nextTick, reactive, ref, toRefs, computed, onMounted, watch, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ClientService,
  ClientStrategyRes,
  FetchOptions,
  ExecuteTerminalActionOptions
} from './client.service'
import ContentAdaptLayout from '../../_components/content-adapt-layout/content-adapt-layout.vue'
import ClientRegisteInfoModal from './modals/client-registe-info-modal.vue'
import ClientShutDownModal from './modals/client-shutdown-modal.vue'
import ClientRebootModal from './modals/client-reboot-modal.vue'
import ClientBlackListModal from './modals/client-blacklist-modal.vue'
import ClientMoveTerminalModal from './modals/client-move-terminal-modal.vue'
import ClientDeleteModal from './modals/client-delete-modal.vue'
import ClientDefencePolicyModal from './modals/client-defence-policy-modal.vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { GroupRefreshManage } from '@/services/group'
import { ElMessage } from 'element-plus'
import { TableDataRowState, TableState } from '@/services/table-memory-pagination'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'

import RemoteAssist from '@/views/dashboard/group/client/modals/remote-assist/remote-assist.vue'
import TerminalInfo from '@/views/dashboard/group/client/terminal-info/terminal-info.vue'
import { Group } from '@/views/dashboard/_type/group'
import { TerminalTypeLabel, TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import MessagePushModal from '@/views/dashboard/group/client/modals/message-push-modal.vue'
import ServiceManage from '@/views/dashboard/group/client/service-manage/service-manage.vue'
import LocationInfo from '@/views/dashboard/group/client/location-info/index.vue'
import FleetMap from '@/views/dashboard/group/client/location-info/fleet-map.vue'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { TerminalLoginStatus } from '@/views/dashboard/_enum/terminal-login-status'
import {
  isClientSupportSsh,
  isClientSupportVnc,
  OS_REMOTE
} from '@/views/dashboard/group/client/modals/remote-assist/remote-assist.service'
import { isVersionGte } from '@/utils/tool'
import StatisticsCards from './_components/statistics-cards.vue'
import type { GroupClientStatCardKey } from './_components/group-client-statistics-cards.types'
import SystemVersionDistributionChart from './_components/system-version-distribution-chart.vue'
import OnlineOfflineTrendChart from './_components/online-offline-trend-chart.vue'
import { HomeService, StatsTerminalRes } from '@/views/dashboard/home/home.service'
import Card from '../../_components/card/card.vue'
import { useLoadMore } from '@/components/LoadMore/useLoadMore'
import GroupClientTerminalListSection from '@/views/dashboard/group/client/group-client-terminal-list-section.vue'
import InOutLog from '@/views/dashboard/notice-manage/in-out-log/in-out-log.vue'
import { GroupClientPageKey } from '@/views/dashboard/group/client/group-client-page.context'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { TimeRange, TimeGranularity, TimeFormat } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { formatTimeStamp, getTimeRangeByEnum } from '@/utils/time'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

const CLIENT_TABLE_COLUMN_KEYS = [
  'user_mark', 'login_status', 'version', 'ip', 'mac', 'system_name',
  'current_startup_time', 'today_online_time', 'last_startup_time', 'last_offline_time', 'remark'
] as const
const COLUMN_I18N_KEYS: Record<typeof CLIENT_TABLE_COLUMN_KEYS[number], string> = {
  user_mark: 'columnUserMark',
  login_status: 'columnTerminalStatus',
  version: 'columnVersion',
  ip: 'columnIp',
  mac: 'columnMac',
  system_name: 'columnSystemName',
  current_startup_time: 'columnCurrentStartupTime',
  today_online_time: 'columnTodayOnlineTime',
  last_startup_time: 'columnLastStartupTime',
  last_offline_time: 'columnLastOfflineTime',
  remark: 'columnRemark'
}

const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth,
}

/** 列表卡片默认仅展示条数；完整列表在抽屉内滚动加载 */
const PREVIEW_LIMIT = 10
const DRAWER_PAGE_SIZE = 20

function isDisabled(row) {
  return getAuthByList(row.auth_status, ModulePermissionKey[row.area_type]) !== TerminalAuthStatus.normal
}

export default defineComponent({
  components: {
    ServiceManage,
    LocationInfo,
    FleetMap,
    TerminalInfo,
    RemoteAssist,
    ContentAdaptLayout,
    ClientRegisteInfoModal,
    ClientShutDownModal,
    ClientRebootModal,
    ClientBlackListModal,
    ClientMoveTerminalModal,
    ClientDeleteModal,
    MessagePushModal,
    ClientDefencePolicyModal,
    StatisticsCards,
    SystemVersionDistributionChart,
    OnlineOfflineTrendChart,
    Card,
    GroupClientTerminalListSection,
    InOutLog
  },
  setup() {
    const { t, locale } = useI18n()

    const status = computed(() => {
      void locale.value
      return {
        options: [
          { value: 1, label: t('groupClient.online') },
          { value: 2, label: t('groupClient.offline') }
        ]
      }
    })
    const AlltableColumns = computed(() => CLIENT_TABLE_COLUMN_KEYS.map(c_key => ({
      c_key,
      c_name: t('groupClient.' + COLUMN_I18N_KEYS[c_key])
    })))
    const injector = useReflectiveInjector([ClientService, HomeService])
    const clientService = injector.get(ClientService)
    const homeService = injector.get(HomeService)

    clientService.isDisabled = isDisabled

    const groupRefreshManage = injector.get(GroupRefreshManage)
    const multipleTable = ref(null)
    const multipleTableDrawer = ref(null)
    const terminalListDrawerVisible = ref(false)
    const inOutLogDrawerVisible = ref(false)
    const inOutLogDrawerTitle = ref('')
    const inOutLogEmbedDefaults = ref<{
      event_types: string
      dateRange: [string, string]
    } | null>(null)
    /** 自定义勾选列时，程序化 toggleRowSelection 会触发 selection-change 且 val 常为 []，勿再 merge 否则本页全选会被立刻清空 */
    const ignoreTableSelectionChangeFromSync = ref(false)
    const CHECKED_COLUMN_LOCAL = JSON.parse(localStorage.getItem('CHECKED_COLUMN_LOCAL'))
    const route = useRoute()
    const terminalInfoVisible = ref(false)
    const serviceManageVisible = ref(false)
    const locationInfoVisible = ref(false)
    const fleetMapVisible = ref(false)
    // 统计数据
    const statistics = reactive({
      totalTerminals: 0,
      onlineCount: 0,
      offlineCount: 0,
      todayJoinCount: 0,
      todayLeaveCount: 0,
      buyCount: 0,
      exceedCount: 0,
      expireCount: 0,
      peripheralsApprovalCount: 0,
      sendFileApprovalCount: 0,
      expire_terminal_count: false,
    })

    // 系统版本分布数据
    const systemVersionData = ref<Array<{ name: string; value: number; system_type?: number }>>([])

    // 在线/离线趋势数据
    const onlineOfflineTrendData = ref<{
      times: string[]
      online: number[]
      offline: number[]
    }>({
      times: [],
      online: [],
      offline: []
    })

    // 时间范围相关
    const timeRange = ref<TimeRange>(TimeRange.DAYS_30)
    const timeRangeValue = ref<[number, number]>(getTimeRangeByEnum(TimeRange.DAYS_30))

    // 主列表：仅首屏 PREVIEW_LIMIT 条，无触底加载
    const previewLoadMoreState = useLoadMore<TableDataRowState<ClientItem>>({
      initialPage: 1,
      pageSize: PREVIEW_LIMIT
    })
    // 抽屉内完整列表：滚动加载
    const drawerLoadMoreState = useLoadMore<TableDataRowState<ClientItem>>({
      initialPage: 1,
      pageSize: DRAWER_PAGE_SIZE
    })


    const data = reactive({
      tableData: [] as TableDataRowState<ClientItem>[],
      terminal_count: 0,
      state: {} as TableState<TableDataRowState<ClientItem>>,
      total: 0,
      searchKeyword: '',
      defenceStrategy: null as ClientStrategyRes,
      checkedColumns: CHECKED_COLUMN_LOCAL || ['computer_name', 'user_mark', 'version', 'login_status', 'ip', 'system_name', 'today_online_time', 'remark'],
      tableColumns: [] as Array<{ c_key: string; c_name: string }>,
      clientModal: {
        MODAL_CLIENT_REGISTE_INFO: false,
        MODAL_CLIENT_SHUTDOWN: false,
        MODAL_CLIENT_REBOOT: false,
        MODAL_CLIENT_BLACKLIST: false,
        MODAL_CLIENT_DELETE: false,
        MODAL_CLIENT_DEFENCE_POLICY: false,
        MODAL_CLIENT_MOVE_TERMINAL: false,
        MODAL_PUSH_MODAL: false
      },
      currentRow: null as ClientItem,
      options: {
        group_id: null,
        login_status: Number(route.query.status) || undefined,
        system_type: Number(route.query.system_type) || undefined,
        key_word: '',
        page: 1,
        limit: 20,
        sort: 'login_status',
        asc: true,
        useMemory: false,
        permission_names: [ModulePermissionKey[AreaType.linux]]
        // system_types: [1, 2, 3, 4]
      },
      blacklist: [],
      loading: true,
      remoteAssistDialog: false
    })

    const serviceManageDrawerTitle = computed(() =>
      t('groupClient.serviceConfigDrawerTitle', { name: data.currentRow?.computer_name ?? '' })
    )

    // 加载统计数据
    function loadStatistics() {
      homeService.initStatsTerminal()
      homeService.updateStatsTerminalDataEvent.subscribe((res: StatsTerminalRes) => {
        statistics.totalTerminals = res.deploy_count || 0
        statistics.onlineCount = res.online_count || 0
        statistics.offlineCount = res.offline_count || 0
        statistics.todayJoinCount = res.today_join_count || 0
        statistics.todayLeaveCount = res.today_leave_count || 0
        statistics.buyCount = res.buy_count || 0
        statistics.exceedCount = res.exceed_count || 0
        statistics.expireCount = res.expire_count || 0
        statistics.peripheralsApprovalCount = res.peripherals_approval_count || 0
        statistics.sendFileApprovalCount = res.send_file_approval_count || 0
        statistics.expire_terminal_count = res.expire_terminal_count>= 0
      })
    }

    // 加载系统版本分布数据
    async function loadSystemVersionDistribution() {
      try {
        const res = await clientService.getTerminalOsDistribution()

        // 转换API返回的数据格式为图表组件需要的格式
        systemVersionData.value = (res.os_version_list || []).map(item => ({
          name: item.system_name || 'Unknown',
          value: item.count || 0,
          system_type: item.system_type
        }))
      } catch (error) {
        console.error('加载系统版本分布数据失败:', error)
        systemVersionData.value = []
      }
    }

    // 加载在线/离线趋势数据
    async function loadOnlineOfflineTrend() {
      try {
        const [start_time, end_time] = timeRangeValue.value
        const granularity = TimeGranularity[timeRange.value] || 'hour'

        // 调用API
        const res = await clientService.getTerminalOnlineTrend({
          start_time: start_time.toString(),
          end_time: end_time.toString(),
          granularity
        })

        // 转换API返回的数据格式为图表组件需要的格式
        const timePoints = res.time_points || []
        const onlineCounts = res.online_counts || []
        const offlineCounts = res.offline_counts || []

        // 格式化时间点显示，参考 virus-trend-chart.vue 的处理方式
        const times = timePoints.map((timestamp: string) => {
          return formatTimeStamp(timestamp, TimeFormat[timeRange.value])
        })

        onlineOfflineTrendData.value = {
          times,
          online: onlineCounts,
          offline: offlineCounts
        }
      } catch (error) {
        console.error('加载在线/离线趋势数据失败:', error)
        onlineOfflineTrendData.value = {
          times: [],
          online: [],
          offline: []
        }
      }
    }

    function handleTimeRangeChange(value: TimeRange, timeRangeArray: [number, number]) {
      timeRange.value = value
      timeRangeValue.value = timeRangeArray
      loadOnlineOfflineTrend()
    }

    /** 主卡片：仅加载首屏 PREVIEW_LIMIT 条（无触底分页） */
    async function loadPreview () {
      data.loading = true
      try {
        previewLoadMoreState.reset()
        await previewLoadMoreState.loadMore(async (page: number, pageSize: number) => {
          const res = await clientService.getListByServer({
            ...data.options,
            page,
            limit: PREVIEW_LIMIT
          })
          const listWithSelected = (res.list || []).map((item: ClientItem) => ({
            ...item,
            $selected: false
          })) as TableDataRowState<ClientItem>[]
          return {
            data: listWithSelected,
            total: res.total || 0,
            terminal_count: res.terminal_count || 0
          }
        })
        data.tableData = previewLoadMoreState.data.value
        data.total = previewLoadMoreState.total.value
        data.terminal_count = previewLoadMoreState.terminal_count.value
        if (!terminalListDrawerVisible.value) {
          clientService.syncVisibleList(data.tableData, data.total)
        }
      } finally {
        data.loading = false
      }
    }

    /** 抽屉内完整列表滚动加载 */
    async function handleDrawerLoadMore () {
      try {
        await drawerLoadMoreState.loadMore(async (page: number, pageSize: number) => {
          const res = await clientService.getListByServer({
            ...data.options,
            page,
            limit: pageSize
          })
          const listWithSelected = (res.list || []).map((item: ClientItem) => ({
            ...item,
            $selected: false
          })) as TableDataRowState<ClientItem>[]
          return {
            data: listWithSelected,
            total: res.total || 0,
            terminal_count: res.terminal_count || 0
          }
        })
        if (terminalListDrawerVisible.value) {
          clientService.syncVisibleList(drawerLoadMoreState.data.value, drawerLoadMoreState.total.value)
        }
      } finally {
        /* drawerLoadMoreState.loading 由 hook 管理 */
      }
    }

    const drawerLoadMoreConfig = computed(() => ({
      callback: handleDrawerLoadMore,
      loading: drawerLoadMoreState.loading.value,
      hasMore: drawerLoadMoreState.hasMore.value,
      disabled: !drawerLoadMoreState.hasMore.value || drawerLoadMoreState.loading.value,
      threshold: 10,
      delay: 100
    }))

    function openTerminalListDrawer () {
      terminalListDrawerVisible.value = true
    }

    /** 统计卡 / 图表：刷新预览与抽屉内列表并必要时打开抽屉 */
    async function openFullTerminalListAfterFilterChange () {
      await loadPreview()
      if (terminalListDrawerVisible.value) {
        drawerLoadMoreState.reset()
        await handleDrawerLoadMore()
      } else {
        terminalListDrawerVisible.value = true
      }
    }

    /** 系统版本分布图点击扇区：按系统类型筛选并打开完整终端列表抽屉 */
    async function handleSystemVersionSliceClick (payload: { system_type: number; name: string }) {
      data.options.useMemory = false
      clientService.cancelSelectAll()
      data.options.system_type = payload.system_type
      await openFullTerminalListAfterFilterChange()
    }

    /**
     * 终端管理首页统计卡：带参打开全量终端抽屉或嵌入加入/离开日志抽屉
     * - 全网数量：查看全部（清在线/离线筛选与系统类型筛选）
     * - 在线/离线：终端状态筛选项与列表一致（login_status 1 / 2）
     * - 今日加入/离开：进出日志列表抽屉 + 事件类型 + 当日时间范围
     */
    async function handleGroupClientStatClick (key: GroupClientStatCardKey) {
      const todayRange: [string, string] = [
        dayjs().format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD')
      ]
      if (key === 'todayJoin') {
        inOutLogEmbedDefaults.value = { event_types: '1', dateRange: todayRange }
        inOutLogDrawerTitle.value = `${t('noticeManage.inOutLog')} [${t('groupClient.todayJoin')}]`
        inOutLogDrawerVisible.value = true
        return
      }
      if (key === 'todayLeave') {
        inOutLogEmbedDefaults.value = { event_types: '2-3-4-5', dateRange: todayRange }
        inOutLogDrawerTitle.value = `${t('noticeManage.inOutLog')} [${t('groupClient.todayLeaveCount')}]`
        inOutLogDrawerVisible.value = true
        return
      }
      data.options.useMemory = false
      clientService.cancelSelectAll()
      if (key === 'total') {
        data.options.login_status = undefined
        data.options.system_type = undefined
      } else if (key === 'online') {
        data.options.login_status = 1
      } else if (key === 'offline') {
        data.options.login_status = 2
      } else {
        return
      }
      await openFullTerminalListAfterFilterChange()
    }

    watch(terminalListDrawerVisible, (visible) => {
      if (visible) {
        nextTick(async () => {
          drawerLoadMoreState.reset()
          await handleDrawerLoadMore()
        })
      } else {
        data.options.system_type = Number(route.query.system_type) || undefined
        data.options.login_status = Number(route.query.status) || undefined
        loadPreview()
      }
    })

    const drawerRows = computed(() => drawerLoadMoreState.data.value)
    const drawerLoadingFlag = computed(() => drawerLoadMoreState.loading.value)
    const drawerHasMore = computed(() => drawerLoadMoreState.hasMore.value)
    const drawerTotalCount = computed(() => drawerLoadMoreState.total.value)

    clientService.onStateChange.subscribe(state => {
      data.state = state
      data.currentRow = data.tableData.find(v => v.device_id === data.currentRow?.device_id)
      ignoreTableSelectionChangeFromSync.value = true
      nextTick(() => {
        // element-plus 1.0.2-beta 数据刷新时 setData 走 cleanSelection（按 rowKey 不清理旧行引用），
        // 内部 selection 会残留旧对象导致 header 全选框状态失真；先清空再按 $selected 重建
        const previewTable = multipleTable.value?.getTable()
        previewTable?.clearSelection()
        data.tableData.forEach(i => previewTable?.toggleRowSelection(i, i.$selected))
        if (terminalListDrawerVisible.value) {
          const drawerTable = multipleTableDrawer.value?.getTable()
          drawerTable?.clearSelection()
          drawerLoadMoreState.data.value.forEach(i =>
            drawerTable?.toggleRowSelection(i, i.$selected))
        }
        nextTick(() => {
          ignoreTableSelectionChangeFromSync.value = false
        })
      })
    })

    /**
     * 通用终端操作函数
     */
    function executeTerminalAction(options: ExecuteTerminalActionOptions) {
      const { serviceMethod, actionName, paramsOverride, onSuccess } = options
      const isSelectAll = data.state.selectAll
      const count = isSelectAll ? data.total : clientService.getSelectedData().length
      const baseParams = {
        execute_type: isSelectAll ? 1 : 0,
        device_list: isSelectAll ? [] : clientService.getSelectedData().map(item => item.device_id),
        group_id: data.options.group_id
      }
      const params = { ...baseParams, ...paramsOverride } as any

      return serviceMethod(params).then(() => {
        ElMessage.success(t('groupClient.executeTerminalDispatchSuccess', { count, action: actionName }))
        onSuccess?.()
      }).catch(() => {
        ElMessage.error(t('groupClient.executeTerminalDispatchFail', { count, action: actionName }))
      })
    }

    async function fetchData (options: FetchOptions, isOperatePage: boolean = false) {
      !isOperatePage && (options.page = 1)

      await loadPreview()
      if (terminalListDrawerVisible.value) {
        drawerLoadMoreState.reset()
        await handleDrawerLoadMore()
      }

      // 同时更新系统版本分布
      loadSystemVersionDistribution()

      clientService.fetchBlackList({ group_id: options.group_id, page: 1, limit: 100 }).then(response => {
        data.blacklist = response.black_list
      })
    }

    function messagePushConfirm(message) {
      executeTerminalAction({
        serviceMethod: clientService.sendMessage.bind(clientService),
        actionName: t('groupClient.terminalActionMessagePush'),
        paramsOverride: { message },
      })
    }

    function selectCancel() {
      data.options.useMemory = false
      clientService.cancelSelectAll()
      fetchData(data.options)
    }

    function getRemoteDisableTip(item: ClientItem) {
      const config = OS_REMOTE[item.system_type]
      if (!config.has_old && !config.has_vnc && !config.has_ssh) {
        return t('groupClient.remoteAssistTipUnsupportedOs')
      }

      if (!config.has_old && !isClientSupportVnc(item.version, item.system_type) && !isClientSupportSsh(item.version, item.system_type)) {
        const { vnc_cversion, ssh_cversion } = config
        const minVersion = isVersionGte(vnc_cversion, ssh_cversion) ? ssh_cversion : vnc_cversion
        return t('groupClient.remoteAssistTipClientVersionRequired', {
          type: TerminalTypeLabel[item.system_type],
          version: minVersion
        })
      }

      if (item.login_status !== TerminalLoginStatus.online) {
        return t('groupClient.remoteAssistTipOffline')
      }

      return null
    }

    function terminalGroupIdSelected(groupId: number) {
      /** 分组未变化时不重复请求：避免「预览 + 抽屉」各挂载一棵分组树时初始各 emit 一次、或与 onMounted 首次拉列表叠成两次 */
      if (data.options.group_id === groupId) {
        return
      }
      data.options.useMemory = false
      clientService.cancelSelectAll()
      data.options.group_id = groupId
      fetchData(data.options)
    }
    function terminalGroupSelected(row: Group) {
      terminalGroupIdSelected(row.group_id)
    }

    // 初始化加载（终端列表首屏由工具栏 GroupTreeSelector 首次选中分组后 terminalGroupIdSelected → fetchData 拉取，避免与 onMounted 的 loadPreview 重复请求 /terminal/list）
    onMounted(() => {
      data.tableColumns = AlltableColumns.value.filter((col: any) => data.checkedColumns.includes(col.c_key))
      loadStatistics()
      loadSystemVersionDistribution()
      loadOnlineOfflineTrend()
    })

    provide(GroupClientPageKey, {
      data,
      clientService,
      fetchData,
      executeTerminalAction,
      ignoreTableSelectionChangeFromSync,
      status,
      AlltableColumns,
      ModulePermissionKey,
      openTerminalListDrawer,
      terminalInfoVisible,
      serviceManageVisible,
      locationInfoVisible,
      fleetMapVisible,
      getRemoteDisableTip,
      terminalGroupIdSelected
    })

    return {
      ...toRefs(data),
      IS_JP_ONLY_BUILD,
      serviceManageDrawerTitle,
      status,
      AlltableColumns,
      statistics,
      systemVersionData,
      onlineOfflineTrendData,
      timeRange,
      drawerLoadMoreConfig,
      drawerLoadMoreState,
      drawerRows,
      drawerLoadingFlag,
      drawerHasMore,
      drawerTotalCount,
      loadPreview,
      handleDrawerLoadMore,
      terminalListDrawerVisible,
      inOutLogDrawerVisible,
      inOutLogDrawerTitle,
      inOutLogEmbedDefaults,
      multipleTableDrawer,
      openTerminalListDrawer,
      handleSystemVersionSliceClick,
      handleGroupClientStatClick,
      PREVIEW_LIMIT,
      TableViewMode,
      handleTimeRangeChange,
      HIDE_FUNC,
      TerminalTypeOptions,
      ModulePermissionKey,
      AreaType,
      multipleTable,
      terminalInfoVisible,
      serviceManageVisible,
      locationInfoVisible,
      fleetMapVisible,
      messagePushConfirm,
      terminalGroupSelected,
      handleRemoteAssistDialogClose() {
        data.remoteAssistDialog = false
      },
      closeClientModal(type: string) {
        data.clientModal[type] = false
      },
      async reloadData () {
        await fetchData(data.options)
      },
      deleteTerminal() {
        clientService.cancelSelect(data.currentRow as TableDataRowState<ClientItem>)
        groupRefreshManage.refreshGroup()
        fetchData(data.options)
      },
      shutDownTer() {
        executeTerminalAction({
          serviceMethod: clientService.shutDownTerminal.bind(clientService),
          actionName: t('groupClient.terminalActionShutDown')
        })
      },
      restartTer() {
        executeTerminalAction({
          serviceMethod: clientService.restartTerminal.bind(clientService),
          actionName: t('groupClient.terminalActionRestart')
        })
      },
      moveTerminal(id: number) {
        const isSelectAll = data.state.selectAll
        executeTerminalAction({
          serviceMethod: clientService.moveTerminal.bind(clientService),
          actionName: t('groupClient.terminalActionMoveGroup'),
          paramsOverride: {
            type: isSelectAll ? 2 : 1,
            new_group_id: id,
            origin_group_id: data.options.group_id
          },
          onSuccess: () => {
            groupRefreshManage.refreshGroup()
            selectCancel()
          }
        })
      },
      registeInfo(info: string) {
        executeTerminalAction({
          serviceMethod: clientService.completeInfo.bind(clientService),
          actionName: t('groupClient.terminalActionCompleteInfo'),
          paramsOverride: { message: info }
        })
      }
    }
  },
})
